import React, { useState, useEffect, useRef } from "react";
import { Upload, RefreshCw } from "lucide-react";
import "./EmailImageUpload.css";
import BaseButton from "../Common/BaseButton";
import { isValidImage } from "../../utils/imageHelper";

const EmailImageUpload = ({
  onImageUpload,
  initialImage = null,
  previewMode = false,
  allowRegenerate = false,
  onRegenerateClick = null,
  label = "Image",
  placeholderText = "Upload an image",
  height = "200px",
  width = "100%",
}) => {
  const [image, setImage] = useState(initialImage);
  const [imageUrl, setImageUrl] = useState("");
  const [imageError, setImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Update local state if initialImage changes from parent
  useEffect(() => {
    // Only update if we're not in the middle of uploading
    // and if there's actually a change in the image value
    if (!isUploading) {
      // For File objects, compare by name, size, and lastModified
      let imagesAreDifferent = false;
      if (initialImage instanceof File && image instanceof File) {
        imagesAreDifferent = 
          initialImage.name !== image.name ||
          initialImage.size !== image.size ||
          initialImage.lastModified !== image.lastModified;
      } else {
        // For strings or other types, use simple reference comparison
        imagesAreDifferent = initialImage !== image;
      }
      
      if (imagesAreDifferent) {
       
        // Only set image if it's a valid image value, otherwise set to null
        setImage(isValidImage(initialImage) ? initialImage : null);
        setImageError(false);
      }
    }
  }, [initialImage, isUploading]);

  // Separate effect to handle image URL generation
  useEffect(() => {
    if (image instanceof File || image instanceof Blob) {
      const url = URL.createObjectURL(image);
      setImageUrl(url);

      return () => URL.revokeObjectURL(url); // Clean up
    } else if (typeof image === "string") {
      setImageUrl(image);
    } else if (!image || !isValidImage(image)) {
      setImageUrl(null);
    }
  }, [image]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Mark that we're uploading to prevent state conflicts
      setIsUploading(true);

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        console.error("File too large, max size is 5MB");
        alert("Image too large. Maximum size is 5MB.");
        setIsUploading(false);

        // Reset the file input to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Update local state immediately so the preview updates right away
      setImage(file);
      
      // Notify parent component with the isImageUpload flag
      if (onImageUpload) {
        console.log("Calling parent onImageUpload with new image data");
        try {
          onImageUpload(file);
          // Reset file input to allow selecting the same file again
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          setTimeout(() => {
            setIsUploading(false);
          }, 100);
        } catch (err) {
          console.error("Error in parent onImageUpload callback:", err);
          setIsUploading(false);
        }
      } else {
        setIsUploading(false);
      }

      // const reader = new FileReader();
      // reader.onloadend = () => {
      //   const imageData = reader.result;

      //   // Update local state
      //   setImage(imageData);
      //   setImageError(false);

      //   // Notify parent component with the isImageUpload flag
      //   if (onImageUpload) {
      //     console.log("Calling parent onImageUpload with new image data");
      //     try {
      //       onImageUpload(imageData);
      //     } catch (err) {
      //       console.error("Error in parent onImageUpload callback:", err);
      //     }
      //   }

      //   // After short delay, reset upload state
      //   setTimeout(() => {
      //     setIsUploading(false);
      //   }, 300);

      //   // Reset the file input to allow selecting the same file again
      //   if (fileInputRef.current) {
      //     fileInputRef.current.value = "";
      //   }
      // };

      // reader.onerror = () => {
      //   console.error("Error reading image file");
      //   setImageError(true);
      //   setIsUploading(false);

      //   // Reset the file input to allow selecting the same file again
      //   if (fileInputRef.current) {
      //     fileInputRef.current.value = "";
      //   }
      // };

      // reader.readAsDataURL(file);
    }
  };

  // // Comprehensive image URL sanitization
  // const getImageUrl = (url) => {
  //   if (!url) return null;

  //   try {
  //     // Data URLs should be returned as-is
  //     if (typeof url === "string" && url.startsWith("data:")) {
  //       return url;
  //     }

  //     // Handle HTTP URLs
  //     if (typeof url === "string") {
  //       // Remove any timestamp parameter if it exists
  //       if (url.includes("&t=")) {
  //         return url.split("&t=")[0];
  //       }

  //       // Remove duplicate query parameters
  //       if (url.includes("?") && url.indexOf("?") !== url.lastIndexOf("?")) {
  //         return url.substring(0, url.lastIndexOf("?"));
  //       }
  //     }

  //     return url;
  //   } catch (error) {
  //     console.error("Error sanitizing image URL:", error);
  //     return url; // Return original URL if sanitization fails
  //   }
  // };

  const handleImageError = () => {
    console.error("Image failed to load:", image);
    setImageError(true);
  };

  // const retryLoadImage = () => {
  // if (!image) return;
  // // Reset error state and try to load image again
  // setImageError(false);
  // // Force re-render of image by adding timestamp
  // const timestampedUrl =
  //   typeof image === "string" && image.startsWith("http")
  //     ? `${image}${image.includes("?") ? "&" : "?"}t=${Date.now()}`
  //     : image;
  // // Temporarily update the image URL with timestamp
  // setImage(timestampedUrl);
  // // After a short delay, revert to original URL
  // setTimeout(() => {
  //   setImage(getImageUrl(timestampedUrl));
  // }, 500);
  // };

  return (
    <div className="email-image-upload-container">
      {!previewMode && (
        <>
          {label && (
            <label className="form-label">
              <strong>{label}</strong>
            </label>
          )}
          <div className="d-flex gap-2 mb-2">
            {allowRegenerate && (
              <BaseButton
                className="outlineprimary"
                children="Regenerate Image"
                imageSrc="/regenerate-image.svg"
                handleClick={
                  onRegenerateClick || (() => console.log("Regenerate clicked"))
                }
              />
            )}
            <label className="btn btn-outline-secondary btn-sm mb-0">
              <Upload size={16} className="me-1" />
              Upload Image
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </label>
          </div>
        </>
      )}
      <div
        className="image-preview-container rounded p-2 d-flex justify-content-center align-items-center"
        style={{
          minHeight: height,
          width: width,
          backgroundColor: "#f8f9fa",
          border: imageError ? "1px solid #dc3545" : "1px solid #dee2e6",
        }}
      >
        <img
          // src={URL.createObjectURL(image)}
          src={imageUrl || "/banner-placeholder.png"}
          alt="Uploaded"
          className="img-fluid rounded"
          onError={handleImageError}
          style={{ maxHeight: "calc(100% - 20px)", maxWidth: "100%" }}
        />
        {/* {image && !imageError ? (
          <img
            src={URL.createObjectURL(image)}
            alt="Uploaded"
            className="img-fluid rounded"
            onError={handleImageError}
            style={{ maxHeight: "calc(100% - 20px)", maxWidth: "100%" }}
          />
        ) : imageError ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-danger p-3 text-center">
            <div className="mb-2">⚠️ Image Error</div>
            <small>The image could not be loaded</small>
            <button
              className="btn btn-sm btn-outline-danger mt-2"
              onClick={retryLoadImage}
            >
              <RefreshCw size={14} className="me-1" /> Retry
            </button>
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
            <img
              src="/img-preview.svg"
              alt="Preview"
              className="img-fluid rounded mb-2"
              style={{ maxHeight: "50px" }}
            />
            <small>{placeholderText}</small>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default EmailImageUpload;
